import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Switch } from 'components/ui/switch';
import { Slider } from 'components/ui/slider';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { InfoTooltip } from 'components/info-tooltip';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ConfigurationPanelProps = {
  enableEnhancement: boolean;
  setEnableEnhancement: (value: boolean) => void;
  relevanceScore: number;
  setRelevanceScore: (value: number) => void;
  recallNumber: number;
  setRecallNumber: (value: number) => void;
  searchType: 'mixed' | 'semantic';
  setSearchType: (value: 'mixed' | 'semantic') => void;
  semanticWeight: number;
  setSemanticWeight: (value: number) => void;
  denseWeight: number;
  setDenseWeight: (value: number) => void;
  keywordWeight: number;
  setKeywordWeight: (value: number) => void;
  rerankModel: boolean;
  setRerankModel: (value: boolean) => void;
  groundTruthTexts: string[];
  setGroundTruthTexts: (value: string[]) => void;
};

export const ConfigurationPanel = ({
  enableEnhancement,
  setEnableEnhancement,
  relevanceScore,
  setRelevanceScore,
  recallNumber,
  setRecallNumber,
  searchType,
  setSearchType,
  semanticWeight,
  setSemanticWeight,
  setDenseWeight,
  setKeywordWeight,
  rerankModel,
  setRerankModel,
  groundTruthTexts,
  setGroundTruthTexts,
}: ConfigurationPanelProps) => {
  const { t } = useTranslation();
  const hasEmptyGroundTruthError =
    groundTruthTexts.length > 0 && groundTruthTexts.every((text) => text.trim() === '');

  const [localSemanticWeight, setLocalSemanticWeight] = useState(semanticWeight);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isDragging) {
      setLocalSemanticWeight(semanticWeight);
    }
  }, [semanticWeight, isDragging]);

  return (
    <Card className="flex w-full min-w-[200px] flex-col overflow-hidden lg:w-[48%]">
      <CardHeader className="flex-shrink-0 pb-4">
        <h2 className="text-2xl font-semibold">{t('CONFIGURE')}</h2>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          className="flex-1 space-y-6 overflow-y-auto pr-6 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgb(209 213 219) transparent' }}
        >
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label htmlFor="enable-enhancement" className="text-sm font-medium">
                  {t('ENABLE_QUERY_ENHANCEMENT')}
                </Label>
                <InfoTooltip content={t('QUERY_ENHANCEMENT_TOOLTIP')} />
              </div>
              <Switch
                id="enable-enhancement"
                checked={enableEnhancement}
                onCheckedChange={setEnableEnhancement}
              />
            </div>

            <div>
              <Label className="mb-3 block text-sm font-medium">{t('RELEVANCE_SCORE')}</Label>
              <div className="flex items-center gap-4">
                <Slider
                  min={0}
                  max={0.95}
                  step={0.01}
                  value={[relevanceScore]}
                  onValueChange={(values) => setRelevanceScore(values[0])}
                  className="flex-1"
                />
                <div className="flex min-w-[70px] items-center justify-center gap-1 rounded-md border bg-background px-3 py-1.5">
                  <span className="text-sm font-semibold">{relevanceScore.toFixed(2)}</span>
                  <span className="text-xs text-muted-foreground">/ 0.95</span>
                </div>
              </div>
            </div>

            <div>
              <Label className="mb-3 block text-sm font-medium">{t('RECALL_NUMBER')}</Label>
              <div className="flex items-center gap-4">
                <Slider
                  min={1}
                  max={20}
                  step={1}
                  value={[recallNumber]}
                  onValueChange={(values) => setRecallNumber(values[0])}
                  className="flex-1"
                />
                <div className="flex min-w-[70px] items-center justify-center gap-1 rounded-md border bg-background px-3 py-1.5">
                  <span className="text-sm font-semibold">{recallNumber}</span>
                  <span className="text-xs text-muted-foreground">/ 20</span>
                </div>
              </div>
            </div>

            <div>
              <Label className="mb-3 block text-sm font-medium">{t('KNOWLEDGE_RETRIEVAL_WEIGHT')}</Label>

              <div className="mb-4 flex gap-6">
                <label className="flex cursor-pointer items-center gap-2">
                  <div className="relative flex h-5 w-5 items-center justify-center">
                    <input
                      type="radio"
                      checked={searchType === 'mixed'}
                      onChange={() => {
                        const initialWeight = 80;
                        setSearchType('mixed');
                        setSemanticWeight(initialWeight);
                        setLocalSemanticWeight(initialWeight);
                        setDenseWeight(initialWeight);
                        setKeywordWeight(100 - initialWeight);
                      }}
                      className="peer h-4 w-4 cursor-pointer appearance-none rounded-full border border-primary transition-colors checked:border-primary"
                    />
                    {searchType === 'mixed' && (
                      <div className="pointer-events-none absolute h-2.5 w-2.5 rounded-full bg-primary transition-all" />
                    )}
                  </div>
                  <span className="text-sm">{t('MIXED_SEARCH')}</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <div className="relative flex h-5 w-5 items-center justify-center">
                    <input
                      type="radio"
                      checked={searchType === 'semantic'}
                      onChange={() => {
                        setSearchType('semantic');
                        setSemanticWeight(100);
                        setLocalSemanticWeight(100);
                        setDenseWeight(100);
                        setKeywordWeight(0);
                      }}
                      className="peer h-4 w-4 cursor-pointer appearance-none rounded-full border border-primary transition-colors checked:border-primary"
                    />
                    {searchType === 'semantic' && (
                      <div className="pointer-events-none absolute h-2.5 w-2.5 rounded-full bg-primary transition-all" />
                    )}
                  </div>
                  <span className="text-sm">{t('SEMANTIC_SEARCH')}</span>
                </label>
              </div>

              {searchType === 'mixed' && (
                <div className="space-y-3">
                  <div className="relative flex h-6 items-center">
                    <div className="pointer-events-none absolute top-1/2 h-1.5 w-full -translate-y-1/2 overflow-hidden rounded-full">
                      <div className="flex h-full">
                        <div
                          className="h-full transition-all duration-75 ease-out"
                          style={{ width: `${localSemanticWeight}%`, backgroundColor: '#124091' }}
                        />
                        <div
                          className="h-full transition-all duration-75 ease-out"
                          style={{ width: `${100 - localSemanticWeight}%`, backgroundColor: '#06b6d4' }}
                        />
                      </div>
                    </div>
                    <div className="relative z-10 w-full">
                      <Slider
                        min={70}
                        max={90}
                        step={1}
                        value={[localSemanticWeight]}
                        onValueChange={(value) => {
                          const semanticPercent = value[0];
                          setIsDragging(true);
                          setLocalSemanticWeight(semanticPercent);
                          setSemanticWeight(semanticPercent);
                          setDenseWeight(semanticPercent);
                          setKeywordWeight(100 - semanticPercent);
                        }}
                        onPointerUp={() => setIsDragging(false)}
                        className="[&_[data-slot=slider-range]]:bg-transparent [&_[data-slot=slider-thumb]]:hidden [&_[data-slot=slider-track]]:h-1.5 [&_[data-slot=slider-track]]:bg-transparent"
                      />
                      <div
                        className="pointer-events-none absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[1px] border-primary bg-white shadow-md transition-all duration-75 ease-out"
                        style={{ left: `${localSemanticWeight}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-muted-foreground">
                      {t('SEMANTIC_LABEL')}{' '}
                      <span className="font-semibold" style={{ color: '#1e40af' }}>
                        {localSemanticWeight}%
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      {t('KEYWORDS_LABEL')}{' '}
                      <span className="font-semibold text-cyan-500">
                        {100 - localSemanticWeight}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label htmlFor="enable-rerank" className="text-sm font-medium">
                  {t('RERANK_MODEL')}
                </Label>
                <InfoTooltip content={t('RERANK_MODEL_TOOLTIP')} />
              </div>
              <Switch
                id="enable-rerank"
                checked={rerankModel}
                onCheckedChange={setRerankModel}
              />
            </div>

            <div>
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label htmlFor="ground-truth-texts" className="text-sm font-medium">
                    {t('GROUND_TRUTH_TEXTS')}
                  </Label>
                  <InfoTooltip content={t('GROUND_TRUTH_TEXTS_TOOLTIP')} />
                </div>
                <Switch
                  id="ground-truth-texts"
                  checked={groundTruthTexts.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setGroundTruthTexts(['']);
                    } else {
                      setGroundTruthTexts([]);
                    }
                  }}
                />
              </div>

              {groundTruthTexts.length > 0 && (
                <>
                  <div className="mb-4 space-y-2">
                    {groundTruthTexts.map((text, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={text}
                          onChange={(e) => {
                            const updatedTexts = [...groundTruthTexts];
                            updatedTexts[index] = e.target.value;
                            setGroundTruthTexts(updatedTexts);
                          }}
                          className={`flex-1 ${hasEmptyGroundTruthError ? 'border-destructive' : ''}`}
                          placeholder={t('ENTER_TEXT')}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            const updatedTexts = groundTruthTexts.filter((_, i) => i !== index);
                            setGroundTruthTexts(updatedTexts);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                    {hasEmptyGroundTruthError && (
                      <p className="text-xs text-destructive">
                        {t('GROUND_TRUTH_EMPTY_ERROR')}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGroundTruthTexts([...groundTruthTexts, ''])}
                    className="mb-4"
                  >
                    + {t('ADD')}
                  </Button>
                </>
              )}

              <div className="rounded-lg border border-border bg-muted/30 p-4 dark:bg-muted/30 dark:border-border dark:text-[#FFFFFF]">
                <div className="flex gap-3">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-primary dark:border-[#FFFFFF]">
                    <span className="text-xs font-semibold text-primary dark:text-[#FFFFFF]">i</span>
                  </div>
                  <div>
                    <h4 className="mb-1 text-sm font-semibold">
                      {t('GROUND_TRUTHS_FOR_RETRIEVAL_TESTING')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t('GROUND_TRUTHS_DESCRIPTION')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
